import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Chip,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { KpiCard } from "../components/KpiCard";
import { TableSkeleton } from "../components/TableSkeleton";
import { tokens } from "../theme";
import { foyerApi } from "../http/foyer";
import { useFormat, type Formatters } from "../i18n/format";
import type { Translate } from "../i18n/translate";
import { errorText } from "../utils/apiError";
import type {
  MeteredScreenReport,
  MeteredUsageReport,
} from "@shared/meteredUsage";
import MeteredMonthlyView from "../components/MeteredMonthlyView";
import { usePermission } from "../auth/access";

/**
 * Fleet-wide metered-usage pacing dashboard (2026-09-14).
 *
 * A metered premise's ad-rotation contract requires its signage screen(s) on
 * for its own monthly target (N hours, or a share of its open hours). Some sites are good at it, some aren't, and by the time anyone
 * notices at month-end it's too late to increase the ad rotation to make up
 * the shortfall. The "This month" tab is the early warning: every metered
 * premise, coloured by whether it can STILL plausibly reach its target even
 * running its screen for every remaining open hour this month. The "Monthly
 * KPI" tab is the scorecard (`MeteredMonthlyView`).
 *
 *   RED    - can't reach the target even at 100% of the open hours left.
 *   YELLOW - reachable, but only by running close to all of what's left -
 *            the danger zone: no real slack remains.
 *   GREEN  - comfortable room at a normal pace.
 *
 * See `@shared/meteredUsage` for the exact math and `meteredUsageReport.ts`
 * for the server-side roll-up this reads.
 */

const STATUSES = ["red", "yellow", "green"] as const;
type Status = (typeof STATUSES)[number];
const isStatus = (s: string): s is Status =>
  (STATUSES as readonly string[]).includes(s);

function statusColors(status: string): { fg: string; bg: string } {
  if (status === "red") return { fg: tokens.error, bg: tokens.errorTint };
  if (status === "yellow")
    return { fg: tokens.warning, bg: tokens.warningTint };
  return { fg: tokens.success, bg: tokens.successTint };
}

function StatusChip({ status }: { status: string }) {
  const { t } = useTranslation("analytics");
  const c = statusColors(status);
  return (
    <Chip
      size="small"
      label={isStatus(status) ? t(`meteredUsagePage.status.${status}`) : status}
      sx={{
        bgcolor: c.bg,
        color: c.fg,
        fontWeight: "fontWeightSemibold",
        border: `1px solid ${c.fg}33`,
      }}
    />
  );
}

function hours(t: Translate, fmt: Formatters, n: number): string {
  return t("analytics:meteredUsagePage.hours", {
    value: fmt.number(n, { maximumFractionDigits: 1 }),
  });
}

/** The screen furthest from its own target (lowest best-case/target) -
 *  what the premise's colour is really about. Targets differ per screen when
 *  one was paired mid-month, so compare ratios, not raw hours. */
function weakestScreen(
  screens: MeteredScreenReport[]
): MeteredScreenReport | undefined {
  const ratio = (s: MeteredScreenReport) =>
    s.targetHours > 0 ? s.ceilingHours / s.targetHours : Infinity;
  return [...screens].sort((a, b) => ratio(a) - ratio(b))[0];
}

export default function MeteredUsagePage() {
  const canPace = usePermission(
    "licences/metered-usage",
    "administrate",
    "nenda"
  );
  const [tab, setTab] = useState<"pacing" | "monthly">(
    canPace ? "pacing" : "monthly"
  );
  const [report, setReport] = useState<MeteredUsageReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation("analytics");
  const fmt = useFormat();
  const h = (n: number) => hours(t, fmt, n);

  useEffect(() => {
    if (!canPace) return;
    let cancelled = false;
    async function load() {
      try {
        const r = await foyerApi.fleetMeteredUsage();
        if (!cancelled) {
          setReport(r);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError(errorText(err, t));
      }
    }
    load();
    const timer = window.setInterval(load, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [canPace, t]);

  const premises = report?.premises ?? [];
  const counts = {
    red: premises.filter((p) => p.status === "red").length,
    yellow: premises.filter((p) => p.status === "yellow").length,
    green: premises.filter((p) => p.status === "green").length,
  };

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("meteredUsagePage.eyebrow")}
        title={t("meteredUsagePage.title")}
        subtitle={t("meteredUsagePage.subtitle")}
      />
      <Tabs
        value={tab}
        onChange={(_e, v: "pacing" | "monthly") => setTab(v)}
        sx={{ mb: 3, borderBottom: `1px solid ${tokens.divider}` }}
      >
        {canPace && (
          <Tab
            value="pacing"
            label={t("meteredUsagePage.tabs.pacing")}
            data-testid="metered-tab-pacing"
          />
        )}
        <Tab
          value="monthly"
          label={t("meteredUsagePage.tabs.monthly")}
          data-testid="metered-tab-monthly"
        />
      </Tabs>
      {tab === "monthly" ? (
        <MeteredMonthlyView />
      ) : (
        <>
          {error && (
            <Typography sx={{ color: tokens.error, mb: 2 }}>{error}</Typography>
          )}
          <Stack
            direction="row"
            sx={{ gap: 3, flexWrap: "wrap", mb: 3, alignItems: "stretch" }}
          >
            <Box sx={{ flex: "1 1 200px", minWidth: 200 }}>
              <KpiCard
                label={t("meteredUsagePage.status.red")}
                value={fmt.number(counts.red)}
                sub={t("meteredUsagePage.kpi.redSub")}
                icon={<ErrorOutlineIcon />}
                tint={tokens.errorTint}
                accent={tokens.error}
              />
            </Box>
            <Box sx={{ flex: "1 1 200px", minWidth: 200 }}>
              <KpiCard
                label={t("meteredUsagePage.status.yellow")}
                value={fmt.number(counts.yellow)}
                sub={t("meteredUsagePage.kpi.yellowSub")}
                icon={<WarningAmberOutlinedIcon />}
                tint={tokens.warningTint}
                accent={tokens.warning}
              />
            </Box>
            <Box sx={{ flex: "1 1 200px", minWidth: 200 }}>
              <KpiCard
                label={t("meteredUsagePage.status.green")}
                value={fmt.number(counts.green)}
                sub={t("meteredUsagePage.kpi.greenSub")}
                icon={<CheckCircleOutlineIcon />}
                tint={tokens.successTint}
                accent={tokens.success}
              />
            </Box>
          </Stack>

          {report === null ? (
            <TableSkeleton rows={5} />
          ) : premises.length === 0 ? (
            <Box
              sx={{
                border: `1px solid ${tokens.divider}`,
                borderRadius: "12px",
                p: 5,
                textAlign: "center",
                bgcolor: tokens.surface,
              }}
              data-testid="metered-usage-empty"
            >
              <Typography
                sx={{ fontWeight: "fontWeightSemibold", color: tokens.ink }}
              >
                {t("meteredUsagePage.empty.title")}
              </Typography>
              <Typography
                variant="bodySm"
                sx={{ color: tokens.inkMuted, mt: 0.5 }}
              >
                {t("meteredUsagePage.empty.body")}
              </Typography>
            </Box>
          ) : (
            <TableContainer
              sx={{
                border: `1px solid ${tokens.divider}`,
                borderRadius: "12px",
              }}
            >
              <Table size="small" data-testid="metered-usage-table">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t("meteredUsagePage.columns.premise")}
                    </TableCell>
                    <TableCell>
                      {t("meteredUsagePage.columns.status")}
                    </TableCell>
                    <TableCell align="right">
                      {t("meteredUsagePage.columns.hoursSoFar")}
                    </TableCell>
                    <TableCell align="right">
                      {t("meteredUsagePage.columns.openHoursLeft")}
                    </TableCell>
                    <TableCell align="right">
                      {t("meteredUsagePage.columns.bestCaseTotal")}
                    </TableCell>
                    <TableCell align="right">
                      {t("meteredUsagePage.columns.screens")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {premises.map((p) => {
                    const noData = p.screens.every((s) => !s.hasData);
                    const outsideHours =
                      p.screens.reduce(
                        (s, x) => s + (x.playedHoursMTD - x.onHoursMTD),
                        0
                      ) / Math.max(1, p.screens.length);
                    const darkCount = p.screens.filter(
                      (s) => s.status !== "green"
                    ).length;
                    return (
                      <TableRow key={p.premiseId} hover>
                        <TableCell>
                          <Typography sx={{ fontWeight: "fontWeightSemibold" }}>
                            {p.premiseName}
                          </Typography>
                          {p.timeZoneInvalid && (
                            <Tooltip
                              title={t(
                                "meteredUsagePage.timezoneInvalidTooltip",
                                {
                                  zone: p.timeZoneInvalid,
                                }
                              )}
                            >
                              <Typography
                                variant="micro"
                                sx={{ color: tokens.warning }}
                              >
                                {t("meteredUsagePage.timezoneInvalid")}
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          <StatusChip status={p.status} />
                          {noData && (
                            <Tooltip
                              title={t("meteredUsagePage.noDataTooltip")}
                            >
                              <Typography
                                variant="micro"
                                sx={{ color: tokens.inkMuted, mt: 0.25 }}
                              >
                                {t("meteredUsagePage.noData")}
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {h(
                            p.screens.reduce((s, x) => s + x.onHoursMTD, 0) /
                              Math.max(1, p.screens.length)
                          )}
                          {outsideHours >= 0.1 && (
                            <Tooltip
                              title={t(
                                "meteredUsagePage.outsideOpeningHoursTooltip"
                              )}
                            >
                              <Typography
                                variant="micro"
                                sx={{ color: tokens.inkMuted }}
                              >
                                {t("meteredUsagePage.outsideOpeningHours", {
                                  hours: h(outsideHours),
                                })}
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {h(
                            Math.min(
                              ...p.screens.map((s) => s.remainingOpenHours)
                            )
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {(() => {
                            const w = weakestScreen(p.screens);
                            return w
                              ? t("meteredUsagePage.bestCase", {
                                  ceiling: h(w.ceilingHours),
                                  target: h(w.targetHours),
                                })
                              : "-";
                          })()}
                        </TableCell>
                        <TableCell align="right">
                          {darkCount > 0
                            ? t("meteredUsagePage.screensAtRisk", {
                                dark: darkCount,
                                total: p.screens.length,
                              })
                            : t("meteredUsagePage.screensHealthy", {
                                count: p.screens.length,
                              })}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </PageContainer>
  );
}
